import objectID from 'bson-objectid';
import blobUtil from 'blob-util';
import * as ActionTypes from '../../../constants';
import {
  setUnsavedChanges,
  closeNewFolderModal,
  closeNewFileModal,
  setSelectedFile,
  createError
} from './ide';
// Cross-module: assets.js also imports from files.js. Both directions are only USED inside thunks
// (deferred to call time), so the cycle is safe — bindings are resolved when the thunks run.
import { maybeDeleteOrphanedAsset } from './assets';

export function appendToFilename(filename, string) {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex === -1) return filename + string;
  return (
    filename.substring(0, dotIndex) + string + filename.substring(dotIndex)
  );
}

export function createUniqueName(name, parentId, files) {
  const siblingFiles = files
    .find((file) => file.id === parentId)
    .children.map((childFileId) =>
      files.find((file) => file.id === childFileId)
    );
  let testName = name;
  let index = 1;
  let existingName = siblingFiles.find((file) => name === file.name);

  while (existingName) {
    testName = appendToFilename(name, `-${index}`);
    index += 1;
    existingName = siblingFiles.find((file) => testName === file.name); // eslint-disable-line
  }
  return testName;
}

export function updateFileContent(id, content) {
  return {
    type: ActionTypes.UPDATE_FILE_CONTENT,
    id,
    content
  };
}

/**
 * Replace the main sketch file's content — how a runnable code block in the
 * AstroTour[AR] reference seeds the sketch it opens (`?snippet=…`, see `utils/docSnippet`).
 *
 * Returns the updated file so the caller can push it into CodeMirror, which doesn't refresh an
 * open buffer on its own; null when there is no `sketch.js` (nothing to seed).
 */
export function setMainSketchContent(content) {
  return (dispatch, getState) => {
    const file = getState().files.find((f) => f.name === 'sketch.js');
    if (!file) return null;
    dispatch(updateFileContent(file.id, content));
    dispatch(setUnsavedChanges(true));
    return { ...file, content };
  };
}

export function createFile(file, parentId) {
  return {
    type: ActionTypes.CREATE_FILE,
    ...file,
    parentId
  };
}

/**
 * Find, or create under root, a folder with the given name and return its id.
 * Idempotent: an existing folder of that name is reused so repeated calls never
 * duplicate it. Used by the "Add Helpers…" / "Add Constellations…" menus to nest
 * their injected files under `helpers/` and `constellations/`. Returns the folder id
 * synchronously (via redux-thunk's dispatch return value), so a caller can pass it
 * straight to `createFile` as the parent.
 *
 * @param {string} name folder name (e.g. 'helpers', 'constellations')
 * @returns a thunk resolving to the folder's id
 */
export function ensureFolder(name) {
  return (dispatch, getState) => {
    const { files } = getState();
    const existing = files.find(
      (f) => f && f.fileType === 'folder' && f.name === name
    );
    if (existing) return existing.id;
    const root = files.find((f) => f && f.name === 'root');
    const id = objectID().toHexString();
    dispatch(
      createFile(
        { name, id, _id: id, content: '', fileType: 'folder', children: [] },
        root.id
      )
    );
    return id;
  };
}

export function submitFile(formProps, files, parentId) {
  const id = objectID().toHexString();
  const file = {
    name: createUniqueName(formProps.name, parentId, files),
    id,
    _id: id,
    url: formProps.url,
    content: formProps.content || '',
    children: []
  };
  return Promise.resolve({ file });
}

export function handleCreateFile(formProps, setSelected = true) {
  return (dispatch, getState) => {
    const state = getState();
    const { files } = state;
    const { parentId } = state.ide;
    return new Promise((resolve) => {
      submitFile(formProps, files, parentId)
        .then((response) => {
          const { file } = response;
          dispatch(createFile(file, parentId));
          dispatch(closeNewFileModal());
          dispatch(setUnsavedChanges(true));
          if (setSelected) {
            dispatch(setSelectedFile(file.id));
          }
          resolve();
        })
        .catch((error) => {
          const { response } = error;
          dispatch(createError(response.data));
          resolve({ error });
        });
    });
  };
}

export function submitFolder(formProps, files, parentId) {
  const id = objectID().toHexString();
  const file = {
    name: createUniqueName(formProps.name, parentId, files),
    id,
    _id: id,
    content: '',
    fileType: 'folder',
    children: []
  };
  return Promise.resolve({ file });
}

export function handleCreateFolder(formProps) {
  return (dispatch, getState) => {
    const state = getState();
    const { files } = state;
    const { parentId } = state.ide;
    return new Promise((resolve) => {
      submitFolder(formProps, files, parentId)
        .then((response) => {
          const { file } = response;
          dispatch(createFile(file, parentId));
          dispatch(closeNewFolderModal());
          dispatch(setUnsavedChanges(true));
          resolve();
        })
        .catch((error) => {
          dispatch(createError(error));
          resolve({ error });
        });
    });
  };
}

export function updateFileName(id, name) {
  return (dispatch) => {
    dispatch(setUnsavedChanges(true));
    dispatch({
      type: ActionTypes.UPDATE_FILE_NAME,
      id,
      name
    });
  };
}

export function deleteFile(id, parentId) {
  return (dispatch, getState) => {
    // Capture the file's media URL BEFORE removing it, so we can clean up its Storage asset.
    const file = (getState().files || []).find((f) => f.id === id);
    const url = file?.url;
    dispatch({
      type: ActionTypes.DELETE_FILE,
      id,
      parentId
    });
    dispatch(setUnsavedChanges(true));
    // Media file → delete the underlying Storage asset if nothing else still references it.
    if (url) dispatch(maybeDeleteOrphanedAsset(url));
  };
}

export function showFolderChildren(id) {
  return {
    type: ActionTypes.SHOW_FOLDER_CHILDREN,
    id
  };
}

export function hideFolderChildren(id) {
  return {
    type: ActionTypes.HIDE_FOLDER_CHILDREN,
    id
  };
}

export function setBlobUrl(file, blobURL) {
  return {
    type: ActionTypes.SET_BLOB_URL,
    id: file.id,
    blobURL
  };
}

export function getBlobUrl(file) {
  if (file.blobUrl) {
    blobUtil.revokeObjectURL(file.blobUrl);
  }

  const fileBlob = blobUtil.createBlob([file.content], { type: 'text/plain' });
  const blobURL = blobUtil.createObjectURL(fileBlob);
  return blobURL;
}
